import * as React from 'react'
import { Clock } from 'lucide-react'
import {
  timeOfDayFrom12,
  twelveHourParts,
  type TimeOfDay,
} from '@lib/validators/time-of-day.validator'
import { cn } from '@/lib/utils'
import { PICKER_CLASSES } from './input'
import { Popover, PopoverTrigger, PopoverContent } from './popover'

interface TimePickerProps {
  /** The time as 24h `HH:MM`, or '' for none. */
  value: TimeOfDay | ''
  /** Called with the new 24h `HH:MM`. A picker only ever emits a real time. */
  onChange: (value: TimeOfDay) => void
  disabled?: boolean
  placeholder?: string
  ariaLabel?: string
  /** Extra classes for the trigger button (e.g. to match a surrounding control height). */
  triggerClassName?: string
}

const pad = (n: number): string => String(n).padStart(2, '0')
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5) // 00, 05, … 55

/**
 * An hour a 12-hour select can produce: 1–12, and nothing a typed string might be.
 *
 * The option values are that list, but the event still arrives as text.
 */
function hourOnClock(raw: string): number | null {
  if (!/^(?:[1-9]|1[0-2])$/.test(raw)) return null
  return Number(raw)
}

/** A minute 0–59. The menu steps by five; a stored time may sit between those steps. */
function minuteOnClock(raw: string): number | null {
  if (!/^\d{1,2}$/.test(raw)) return null
  const minute = Number(raw)
  return minute <= 59 ? minute : null
}

const selectClass =
  'h-9 w-[72px] rounded-md border border-line-strong bg-surface px-2.5 font-mono text-control text-ink tabular-nums hover:border-ink-subtle focus-visible:focus-field'

/** A time picker: a button showing the chosen time opens hour/minute/AM–PM
 * selects. Works in 24h `HH:MM` strings. */
export function TimePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Set time',
  ariaLabel,
  triggerClassName,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false)
  // `hour12` stays null while unset, so the hour select shows a placeholder and
  // picking any hour (including 12) commits. A minute or AM/PM pick before that
  // still composes a time, from 12.
  const face = value === '' ? null : twelveHourParts(value)
  const hour12 = face === null ? null : face.hour12
  const minute = face === null ? 0 : face.minute
  const pm = face === null ? true : face.pm
  const effectiveHour = hour12 ?? 12
  const label = value === '' ? placeholder : `${String(effectiveHour)}:${pad(minute)} ${pm ? 'PM' : 'AM'}`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(PICKER_CLASSES, 'w-auto min-w-[132px]', value === '' && 'text-ink-muted', triggerClassName)}
        >
          <Clock className="h-4 w-4 shrink-0 text-ink-muted" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex items-center gap-2">
        <select
          className={selectClass}
          aria-label="Hour"
          value={hour12 ?? ''}
          onChange={(e) => {
            const hour = hourOnClock(e.target.value)
            if (hour === null) return
            onChange(timeOfDayFrom12(hour, minute, pm))
          }}
        >
          {hour12 === null ? (
            <option value="" disabled>
              –
            </option>
          ) : null}
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span className="font-mono text-ink-muted">:</span>
        <select
          className={selectClass}
          aria-label="Minute"
          value={minute}
          onChange={(e) => {
            const next = minuteOnClock(e.target.value)
            if (next === null) return
            onChange(timeOfDayFrom12(effectiveHour, next, pm))
          }}
        >
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {pad(m)}
            </option>
          ))}
        </select>
        <select
          className={selectClass}
          aria-label="AM or PM"
          value={pm ? 'PM' : 'AM'}
          onChange={(e) => {
            if (e.target.value !== 'AM' && e.target.value !== 'PM') return
            onChange(timeOfDayFrom12(effectiveHour, minute, e.target.value === 'PM'))
          }}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </PopoverContent>
    </Popover>
  )
}
